import { Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  IBreadcrumbsAndMenu,
  ISetAttributeSetting,
} from "../../../types/global";

import { useState } from "react";
import { useSnackbar } from "notistack";
import { useSearchParams } from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import { useModal } from "../../../hooks/use-modal";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";

import {
  SetItemAttributeQuery,
  SetItemAttributesQuery,
  useSetItemAttributeCreateMutation,
  useSetItemAttributeDeleteMutation,
  useSetItemAttributeQuery,
  useSetItemAttributeUpdateMutation,
  useSetItemAttributesQuery,
} from "../../../generated/general";

import SetAttributeTable from "../../../components/Table/Setting/Global/SetAttributeTable";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import ConfirmationModal from "../../../components/UI/Modal/ConfirmationModal";
import SetAttributeModal from "../../../components/UI/Modal/SetAttributeModal";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";

import AddIcon from "@mui/icons-material/Add";

const SetAttributeSetting = () => {
  const { t } = useTranslation();
  const [deletingId, setDeletingId] = useState<number>();

  let [searchParams, setSearchParams] = useSearchParams();

  const id = searchParams.get("id");

  const setSearchParamsHandler = (key: string, value: string) => {
    searchParams.set(key, value);
    setSearchParams(searchParams);
  };

  const removeSearchParamsHandler = (key: string) => {
    searchParams.delete(key);
    setSearchParams(searchParams);
  };

  const { modal, openModalHandler, closeModalHandler } = useModal(
    undefined,
    () => removeSearchParamsHandler("id")
  );

  const { enqueueSnackbar } = useSnackbar();

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("setting.index"),
      to: "/setting",
    },
    {
      name: t("setting.inventory.index"),
      to: "/setting/inventory",
    },
    {
      name: t("inventory.items.set_attribute"),
    },
  ];

  const graphQLClientGeneral: GraphQLClient =
    createGraphQLClientWithMiddleware("general");

  const { data: rowData, refetch: refetchRowData } =
    useSetItemAttributesQuery<SetItemAttributesQuery>(graphQLClientGeneral);

  const { data, isLoading, isSuccess } =
    useSetItemAttributeQuery<SetItemAttributeQuery>(
      graphQLClientGeneral,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: Boolean(id),
      }
    );

  const { mutateAsync: createSetAttribute } =
    useSetItemAttributeCreateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: updateSetAttribute } =
    useSetItemAttributeUpdateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: deleteSetAttribute } =
    useSetItemAttributeDeleteMutation<Error>(graphQLClientGeneral);

  async function onAddHandler(data: ISetAttributeSetting) {
    try {
      await createSetAttribute({
        createInput: data,
      });
      enqueueSnackbar(`เพิ่ม${t("setting.inventory.attribute")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`เพิ่ม${t("setting.inventory.attribute")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  }

  const onEditHandler = (id?: number) => {
    if (id) {
      setSearchParamsHandler("id", id.toString());
      openModalHandler();
    }
  };

  const onUpdateHandler = async (data: ISetAttributeSetting) => {
    try {
      const { id, item_attribute_list, ...otherData } = data;
      await updateSetAttribute({
        uniqueInput: {
          id: id,
        },
        updateInput: otherData,
      });
      enqueueSnackbar(`แก้ไข${t("setting.inventory.attribute")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`แก้ไข${t("setting.inventory.attribute")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const onDeleteHandler = (id?: number) => {
    if (id) {
      setDeletingId(id);
      openConfirmationHandler();
    }
  };

  const deleteActionHandler = async () => {
    try {
      await deleteSetAttribute({
        uniqueInput: {
          id: deletingId,
        },
      });
      setDeletingId(undefined);
      enqueueSnackbar(`ลบ${t("setting.inventory.attribute")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`ลบ${t("setting.inventory.attribute")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const {
    modal: confirmation,
    openModalHandler: openConfirmationHandler,
    closeModalHandler: closeConfirmationHandler,
    submitModalHandler: submitConfirmationHandler,
  } = useModal(deleteActionHandler);

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ display: "flex", justifyContent: "space-between", my: 3 }}>
        <Typography variant="h5">{t("setting.inventory.attribute")}</Typography>
        <CustomizedButton
          title={t("button.add") + t("setting.inventory.attribute")}
          variant="contained"
          onClick={openModalHandler}
          sx={{ textTransform: "none" }}
          endIcon={<AddIcon />}
        />
      </Box>
      <SetAttributeTable
        data={rowData?.SetItemAttributes || []}
        onEditHandler={onEditHandler}
        onDeleteHandler={onDeleteHandler}
      />
      <SetAttributeModal
        open={modal}
        closeModalHandler={closeModalHandler}
        onAddHandler={onAddHandler}
        onUpdateHandler={onUpdateHandler}
        data={data?.SetItemAttribute}
        isLoading={isLoading}
        isSuccess={isSuccess}
      />
      <ConfirmationModal
        title={`ยืนยันการลบ${t("setting.inventory.attribute")}`}
        open={confirmation}
        handleClose={closeConfirmationHandler}
        action={submitConfirmationHandler}
      />
    </>
  );
};

export default SetAttributeSetting;
